import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../../../utils/apiConfig";
import SubPageLayout from "../../../components/shared/SubPageLayout";

import logger from '../../../utils/logger';

const RelationshipManagement = () => {
  const { householdId } = useParams();
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHouseholdData = async () => {
      try {
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}`,
        );
        setHousehold(response.data);
      } catch (err) {
        logger.error("Error fetching household data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHouseholdData();
  }, [householdId]);

  if (loading) {
    return (
      <SubPageLayout
        title="Loading..."
        subtitle="View and manage household relationship details"
        breadcrumbItems={[
          { label: "Advisor Portal", path: "/advisor-portal" },
          { label: "Client Database", path: "/advisor-portal/client-database" },
          {
            label: "Household Profile",
            path: `/advisor-portal/client-database/household-profile/${householdId}`,
          },
          {
            label: "Relationship Management",
            path: `/advisor-portal/client-database/household-profile/${householdId}/relationship`,
          },
        ]}
      >
        <div className="text-center py-4">Loading...</div>
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      title={`${household?.members[0]?.lastName || "Household"} Relationship Management`}
      subtitle="View and manage household relationship details"
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Client Database", path: "/advisor-portal/client-database" },
        {
          label: "Household Profile",
          path: `/advisor-portal/client-database/household-profile/${householdId}`,
        },
        {
          label: "Relationship Management",
          path: `/advisor-portal/client-database/household-profile/${householdId}/relationship`,
        },
      ]}
    >
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
        {/* Relationship Summary */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              Relationship Summary
            </h3>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              Active Household
            </span>
          </div>
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Client Since
              </label>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                2020
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">4 years</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Last Review
              </label>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                Jan 2024
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">2 weeks ago</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Next Review
              </label>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                Apr 2024
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">In 3 months</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Engagement Score
              </label>
              <div className="mt-1 text-2xl font-semibold text-green-600">
                85%
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">Above Average</div>
            </div>
          </div>
        </div>

        {/* Engagement Metrics */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
            Household Engagement Metrics
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Meeting Attendance
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">90%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full"
                  style={{ width: "90%" }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Document Completion
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">85%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: "85%" }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Communication Response
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">95%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full"
                  style={{ width: "95%" }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Portal Usage
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">75%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-yellow-600 h-2.5 rounded-full"
                  style={{ width: "75%" }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Milestones */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
            Key Relationship Milestones
          </h3>
          <div className="flow-root">
            <ul className="-mb-8">
              <li>
                <div className="relative pb-8">
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  ></span>
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                        <span className="text-white text-sm">✓</span>
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          Initial Financial Plan Completed
                        </p>
                      </div>
                      <div className="whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-500">
                        <time dateTime="2020-03-15">Mar 2020</time>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Feedback & Satisfaction */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
            Household Feedback & Satisfaction
          </h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Latest Survey Results
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Overall Satisfaction
                  </span>
                  <span className="text-sm font-medium text-green-600">
                    4.8/5.0
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Would Recommend</span>
                  <span className="text-sm font-medium text-green-600">
                    Yes
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Service Quality</span>
                  <span className="text-sm font-medium text-green-600">
                    4.7/5.0
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Recent Feedback
              </h4>
              <div className="text-sm text-gray-600 dark:text-gray-400 italic">
                "Excellent service and very responsive team. Always providing
                clear and helpful advice."
              </div>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                Received: January 2024
              </div>
            </div>
          </div>
        </div>

        {/* Action Items */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              Relationship Action Items
            </h3>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Action Item
            </button>
          </div>
          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <div className="grid grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Action Item
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter action item..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Priority
                  </label>
                  <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                    <option value="">Select Priority</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Due Date
                  </label>
                  <input
                    type="date"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </label>
                  <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default RelationshipManagement;
